import { GroupedVersions } from '@/types';
import { useEffect, useMemo, useState } from 'react';
import { NoVersions } from './NoVersions';
import { VersionsList } from './VersionsList';
import { VersionsLoader } from './VersionsLoader';
import { GetVersionsQuery, useGetVersionsQuery } from '@/__generated__/graphql';
import { DocumentNode } from '@apollo/client';
import { convertTimeStampToDate } from '@/utils';

type VersionHistoryProps = {
  structureId: string;
  collectionName: string;
  referenceId?: string;
  onClose: () => void;
  disableClose?: boolean;
  allowRestore?: boolean;
  refetchDocuments?: DocumentNode[];
  isESVersion?: boolean;
};

const ESVersionTypes = new Set(['transition period', 'pof calculation', 'cof calculation']);

export const VersionHistory = ({
  structureId,
  referenceId,
  collectionName,
  onClose,
  disableClose,
  allowRestore,
  refetchDocuments,
  isESVersion = false,
}: VersionHistoryProps) => {
  const [versions, setVersions] = useState<GetVersionsQuery['allVersions']>([]);
  const { loading, refetch, data } = useGetVersionsQuery({
    variables: {
      input: {
        structureId,
        collectionName,
        ...(referenceId ? { referenceId } : {}),
      },
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data?.allVersions) {
      setVersions(data.allVersions);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [structureId, referenceId, collectionName, refetch]);

  const groupedVersions = useMemo(() => {
    const grouped: GroupedVersions = {};
    versions
      .filter((version) => {
        if (isESVersion) {
          return ESVersionTypes.has(version.lastModifiedFor.toLowerCase());
        }
        return version;
      })
      .forEach((version) => {
        const date = convertTimeStampToDate(version.createdAt)
          ?.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
          .toString();
        if (!grouped[date ?? '']) {
          grouped[date ?? ''] = [];
        }
        grouped[date ?? ''].push(version);
      });

    Object.keys(grouped).forEach((date) => {
      grouped[date].sort(
        (a, b) =>
          (convertTimeStampToDate(b.createdAt)?.getTime() ?? 0) -
          (convertTimeStampToDate(a.createdAt)?.getTime() ?? 0)
      );
    });

    return grouped;
  }, [versions]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedVersions).sort((a, b) => {
      const dateA = convertTimeStampToDate(a)?.getTime();
      const dateB = convertTimeStampToDate(b)?.getTime();
      return (dateB ?? 0) - (dateA ?? 0);
    });
  }, [groupedVersions]);

  return (
    <>
      {loading && <VersionsLoader />}
      {!loading && versions.length === 0 && <NoVersions />}
      {!loading && versions.length > 0 && (
        <VersionsList
          groupedVersions={groupedVersions}
          days={sortedDates}
          onClose={onClose}
          disableClose={disableClose}
          allowRestore={allowRestore}
          refetchDocuments={refetchDocuments}
          isESVersion={isESVersion}
        />
      )}
    </>
  );
};
